import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Switch } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import Add from '@material-ui/icons/Add';
import clsx from 'clsx';
import { format, isSameDay } from 'date-fns';

import {
  useAddHolidaysMutation,
  useChangeHolidaysDeliveryMutation,
  useDeleteHolidaysMutation,
  useSetDeliveryDayByPostCodeMutation,
} from '../../../../api/company';
import { DeliveryDay } from '../../../../api/company/types';
import { useMeQuery } from '../../../../api/user';
import { HolidayDate } from '../../../../api/user/types';
import { ThemedButton } from '../../../../shared/components/themed-button';
import { MultiDatePicker } from '../../../../shared/components/multi-date-picker';
import { OverlayModal } from '../../../../shared/components/overlay-modal';
import { useScreenSize } from '../../../../shared/hooks/use-screen-size';

import { useStyles } from './style';

interface Props {
  days: DeliveryDay[];
}

export const HolidaySettings: React.FC<Props> = ({ days }) => {
  const classes = useStyles();
  const { isDesktop, isMobile, isTabletLandscape } = useScreenSize();
  const { push } = useHistory();

  const [isHolidaysModalShown, setIsHolidaysModalShown] = useState(false);

  const [localHolidays, setLocalHolidays] = useState<Partial<HolidayDate>[]>([]);

  const { data: user } = useMeQuery();
  const [setDeliveryDay] = useSetDeliveryDayByPostCodeMutation();
  const [changeHolidaysDelivery, { isLoading: changeHolidaysLoading }] = useChangeHolidaysDeliveryMutation();
  const [onDeleteHolidayDate] = useDeleteHolidaysMutation();
  const [onAddHolidayDate] = useAddHolidaysMutation();

  const handleHolidaysSwitch = () => {
    changeHolidaysDelivery();
  };

  const openAddHolidaysPage = () => {
    push('/account/add-holidays');
  };

  const onCloseHolidayModal = () => {
    setLocalHolidays(user?.holidays || []);
    setIsHolidaysModalShown(false);
  };

  const onRemoveHoliday = (day?: string) => {
    setLocalHolidays((prev) => prev.filter((el) => el.holiday_date !== day));
  };

  const onSaveHolidays = () => {
    if (user?.holidays?.length) {
      const holidaysToDelete = user.holidays.filter(
        (el) => !localHolidays.some((item) => isSameDay(new Date(item.holiday_date as string), new Date(el.holiday_date))),
      );
      const holidaysToAdd = localHolidays.filter(
        (el) => !user?.holidays?.some((item) => isSameDay(new Date(item.holiday_date), new Date(el.holiday_date as string))),
      );
      holidaysToAdd.length &&
        onAddHolidayDate(holidaysToAdd.map((day) => format(new Date(day.holiday_date as string), 'dd/MM/yyyy')).join(','));
      holidaysToDelete.length && onDeleteHolidayDate(holidaysToDelete.map((day) => day.id));
    } else {
      onAddHolidayDate(localHolidays.map((day) => format(new Date(day.holiday_date as string), 'dd/MM/yyyy')).join(','));
    }
    onCloseHolidayModal();
  };

  const onSetHolidays = (dates: string[] | Date[]) => {
    setLocalHolidays(
      dates.map((el) => ({
        holiday_date: el.toString(),
      })),
    );
  };

  useEffect(() => {
    user?.holidays && setLocalHolidays(user.holidays);
  }, [user?.holidays]);

  return (
    <div className={classes.root}>
      <div className={classes.holidaySettingsView}>
        <div className={classes.holidaysBlock}>
          <div className={classes.holidaysLabel}>HOLIDAYS (NO DELIVERY)</div>
          <Switch
            checked={!!user?.holidays_delivery}
            onChange={handleHolidaysSwitch}
            disabled={changeHolidaysLoading}
            color='primary'
            classes={{
              thumb: classes.thumb,
              track: classes.track,
              checked: classes.checked,
            }}
          />
        </div>
        {!!user?.holidays_delivery && (
          <div className={classes.holidaysContent}>
            <div className={classes.holidaysContentHead}>
              <div className={classes.holidaysTitle}>Add holidays of which there will be no delivery</div>
              <div
                className={clsx(classes.addBtn, classes.addBtnHolidays)}
                onClick={isDesktop || isTabletLandscape ? setIsHolidaysModalShown.bind(null, true) : openAddHolidaysPage}
              >
                <Add className={classes.addIcon} />
                {isMobile ? 'ADD' : 'Add Days'}
              </div>
            </div>
            <div>
              {user?.holidays?.map((el) => (
                <div key={el.id} className={classes.dateItem}>
                  {format(new Date(el.holiday_date), 'dd.LL')}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {isHolidaysModalShown && (
        <OverlayModal boxClassName={classes.holidaysModal} isOpen={isHolidaysModalShown} onClose={onCloseHolidayModal}>
          <>
            <div className={classes.holidaysModalHead}>
              <h3 className={classes.holidaysModalTitle}>Add Holidays</h3>
              <div className={classes.holidaysSubtitle}>There will be no delivery on the specified days for the year</div>
            </div>
            <div className={classes.holidaysModalContent}>
              <div>
                <MultiDatePicker dates={localHolidays.map((el) => el.holiday_date as string)} onSetDates={onSetHolidays} />
              </div>
              <div>
                <div className={classes.holidaysListHead}>
                  <div className={classes.holidaysListTitle}>SPECIFIED DAYS</div>
                  <div>{localHolidays.length}</div>
                </div>
                <div className={classes.holidaysListBox}>
                  {localHolidays.map((day, idx) => (
                    <div key={idx} className={classes.holidaysCell}>
                      {day.holiday_date ? format(new Date(day.holiday_date), 'dd.LL') : ''}
                      <div className={classes.closeIconWrap} onClick={onRemoveHoliday.bind(null, day.holiday_date)}>
                        <Close className={classes.closeIcon} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className={classes.holidaysBtns}>
              <ThemedButton onClick={onSaveHolidays} title='Save' isSmall={true} width={90} />
              <ThemedButton onClick={onCloseHolidayModal} title='Cancel' isSmall={true} buttonStyle='secondary' width={90} />
            </div>
          </>
        </OverlayModal>
      )}
    </div>
  );
};
